import { useState } from "react"
import { Dialog, HStack, Portal, Separator, Table, VStack } from "@chakra-ui/react"
import { FiShield } from "react-icons/fi"
import { Button, Text, toaster } from "@/components"
import { useAuth } from "@/contexts"
import { getIntegrityCheck, getCompanyIntegrityCheck } from "@/api/legacyBridge"

type DealIntegrityResult = Awaited<ReturnType<typeof getIntegrityCheck>>
type CompanyIntegrityResult = Awaited<ReturnType<typeof getCompanyIntegrityCheck>>
type IntegrityResult = DealIntegrityResult | CompanyIntegrityResult

type Props =
  | { type: "deal"; dealIdStr: string }
  | { type: "company"; companyId: number }

const ADMIN_EMAIL = "sean@esofund.com"

export const IntegrityCheckButton = (props: Props) => {
  const { user } = useAuth()
  const [isChecking, setIsChecking] = useState(false)
  const [result, setResult] = useState<IntegrityResult | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  if (user?.email !== ADMIN_EMAIL) return null

  const handleCheck = async () => {
    setIsChecking(true)
    try {
      const check =
        props.type === "deal"
          ? await getIntegrityCheck(props.dealIdStr)
          : await getCompanyIntegrityCheck(props.companyId)

      if (check.inSync) {
        toaster.create({
          title: "In sync with legacy DB",
          type: "success",
          duration: 3000,
        })
      } else {
        setResult(check)
        setIsOpen(true)
      }
    } catch (err: any) {
      toaster.create({
        title: "Integrity check failed",
        description: err?.response?.data?.error || err?.message || "Unknown error",
        type: "error",
        duration: 4000,
      })
    } finally {
      setIsChecking(false)
    }
  }

  const isDealResult = (r: IntegrityResult): r is DealIntegrityResult =>
    "mismatchedFields" in r

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={handleCheck}
        loading={isChecking}
        loadingText="Checking..."
        gap={1.5}
      >
        <FiShield />
        Check Sync
      </Button>

      <Dialog.Root
        open={isOpen}
        onOpenChange={(e) => !e.open && setIsOpen(false)}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="680px">
              <VStack align="start" gap={4} p={6}>
                <HStack gap={2}>
                  <Text fontSize="lg" fontWeight="bold" color="orange.500">
                    ⚠️ Out of Sync with Legacy DB
                  </Text>
                </HStack>

                {result && isDealResult(result) && (
                  <>
                    <Text fontSize="sm" color="text.muted">
                      Deal <strong>{result.dealIdStr}</strong> — {result.mismatchedFields.length} field(s) differ between v3 and legacy
                    </Text>
                    {result.legacyOGLastModDate && (
                      <Text fontSize="xs" color="text.muted">
                        Legacy last modified: {new Date(result.legacyOGLastModDate).toLocaleString()}
                        {result.legacySource ? ` (source: ${result.legacySource})` : ""}
                      </Text>
                    )}
                    <Separator />
                    <Table.Root size="sm" variant="outline" width="100%">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Field</Table.ColumnHeader>
                          <Table.ColumnHeader>v3 Value</Table.ColumnHeader>
                          <Table.ColumnHeader>Legacy Value</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {result.mismatchedFields.map((f) => (
                          <Table.Row key={f.field}>
                            <Table.Cell fontFamily="mono" fontSize="xs">{f.field}</Table.Cell>
                            <Table.Cell fontSize="xs" color="green.600">
                              {f.v3Value == null ? <em style={{ color: "#aaa" }}>null</em> : String(f.v3Value)}
                            </Table.Cell>
                            <Table.Cell fontSize="xs" color="red.500">
                              {f.legacyValue == null ? <em style={{ color: "#aaa" }}>null</em> : String(f.legacyValue)}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </>
                )}

                {result && !isDealResult(result) && (
                  <>
                    <Text fontSize="sm" color="text.muted">
                      Company <strong>{result.companyName}</strong>
                      {result.legacyId ? ` (legacy ID: ${result.legacyId})` : " (no legacy ID mapped)"}
                    </Text>
                    <Separator />
                    <Table.Root size="sm" variant="outline" width="100%">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Data Type</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">v3 Count</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Legacy Count</Table.ColumnHeader>
                          <Table.ColumnHeader>Status</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {result.items.map((item) => (
                          <Table.Row key={item.label}>
                            <Table.Cell>{item.label}</Table.Cell>
                            <Table.Cell textAlign="right" color="green.600">{item.v3Count}</Table.Cell>
                            <Table.Cell textAlign="right" color="red.500">{item.legacyCount}</Table.Cell>
                            <Table.Cell fontSize="xs">
                              {item.inSync ? "✅ In sync" : `⚠️ Diff: ${item.v3Count - item.legacyCount > 0 ? "+" : ""}${item.v3Count - item.legacyCount}`}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </>
                )}

                <HStack mt={2} justify="flex-end" width="100%">
                  <Button variant="outline" onClick={() => setIsOpen(false)}>
                    Close
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
